// Whiteboard engine — drawing annotations: the engineering-drawing symbols a
// SOLIDWORKS-style drawing carries, drawn as vectors (sharp in the PDF):
//   dimension  a linear dimension with its tolerance (± symmetric, bilateral,
//              limits, basic) and Ø / R / SØ prefix
//   fcf        a geometric tolerance — feature control frame: one of the 14
//              GD&T characteristics, Ø zone, tolerance, material modifier,
//              datum references A | B | C with their modifiers
//   datum      a datum feature: boxed letter, leader, datum triangle
//   finish     surface texture: basic / material removal required / prohibited,
//              roughness value and method
//   weld       weld symbol: reference line, arrow, fillet / V-groove / square /
//              bevel on the arrow side, other side or both, size, length,
//              weld-all-around, field weld, tail
//   balloon    item balloon with leader and dot
//   center     centre mark
// A symbol is laid out in its own frame: the element's box (x, y, w, h) holds
// the symbol; `leader` (relative to x, y) is where its leader ends. Everything
// scales with fontSize. Pure: the suite runs it in Node.

import { textWidth } from './text.js';

export const GDT_CHARS = [
  ['straightness', 'Straightness'], ['flatness', 'Flatness'], ['circularity', 'Circularity'], ['cylindricity', 'Cylindricity'],
  ['profileLine', 'Profile of a line'], ['profileSurface', 'Profile of a surface'], ['angularity', 'Angularity'],
  ['perpendicularity', 'Perpendicularity'], ['parallelism', 'Parallelism'], ['position', 'Position'],
  ['concentricity', 'Concentricity'], ['symmetry', 'Symmetry'], ['runout', 'Circular runout'], ['totalRunout', 'Total runout'],
];
export const SYMBOL_KINDS = [
  { id: 'dimension', label: 'Dimension with tolerance', keys: '' },
  { id: 'fcf', label: 'Geometric tolerance (feature control frame)' },
  { id: 'datum', label: 'Datum feature' },
  { id: 'finish', label: 'Surface finish' },
  { id: 'weld', label: 'Weld symbol' },
  { id: 'balloon', label: 'Balloon' },
  { id: 'center', label: 'Centre mark' },
];

export const DEFAULT_PARAMS = {
  dimension: { value: '25.00', prefix: '', tolType: 'sym', plus: '0.05', minus: '0.05', offset: -40 },
  fcf: { char: 'position', dia: true, tol: '0.05', mod: 'M', d1: 'A', d2: 'B', d3: 'C' },
  datum: { letter: 'A' },
  finish: { finishType: 'removal', value: 'Ra 1.6', method: '' },
  weld: { wtype: 'fillet', side: 'arrow', size: '6', length: '', around: false, field: false, tail: '' },
  balloon: { num: '1' },
  center: {},
};

const K = 0.5522847498;
const circle = (cx, cy, r) => [
  ['M', cx + r, cy], ['C', cx + r, cy + r * K, cx + r * K, cy + r, cx, cy + r], ['C', cx - r * K, cy + r, cx - r, cy + r * K, cx - r, cy],
  ['C', cx - r, cy - r * K, cx - r * K, cy - r, cx, cy - r], ['C', cx + r * K, cy - r, cx + r, cy - r * K, cx + r, cy], ['Z'],
];
const seg = (x1, y1, x2, y2) => [['M', x1, y1], ['L', x2, y2]];
const poly = (pts, close = true) => [['M', pts[0][0], pts[0][1]], ...pts.slice(1).map((p) => ['L', p[0], p[1]]), ...(close ? [['Z']] : [])];
// A filled arrowhead with its tip at (tx, ty), pointing along angle a.
function arrowHead(tx, ty, a, len, half) {
  const bx = tx - Math.cos(a) * len; const by = ty - Math.sin(a) * len;
  const nx = -Math.sin(a) * half; const ny = Math.cos(a) * half;
  return poly([[tx, ty], [bx + nx, by + ny], [bx - nx, by - ny]]);
}
const tw = (t, size) => textWidth(String(t || ''), { size, font: 'sans' });
const T = (text, x, y, size, extra = {}) => ({ k: 'text', lines: [{ text: String(text), x, y, width: tw(text, size) }], size, font: 'sans', bold: false, italic: false, ...extra });
// centred text: x is the centre, y the middle of the cap height
const Tc = (text, cx, cy, size, extra) => T(text, cx - tw(text, size) / 2, cy + size * 0.36, size, extra);

// The 14 GD&T characteristics, in a square of side g centred on (cx, cy).
export function gdtGlyph(char, cx, cy, g) {
  const P = (x, y) => [cx + x * g, cy + y * g];
  const L = (a, b) => { const p = P(...a); const q = P(...b); return seg(p[0], p[1], q[0], q[1]); };
  const fill = [];
  let ops = [];
  switch (char) {
    case 'straightness': ops = L([-0.5, 0], [0.5, 0]); break;
    case 'flatness': ops = poly([P(-0.5, 0.22), P(0.28, 0.22), P(0.5, -0.22), P(-0.28, -0.22)]); break;
    case 'circularity': ops = circle(cx, cy, 0.42 * g); break;
    case 'cylindricity': ops = [...circle(cx, cy, 0.3 * g), ...L([-0.5, 0.48], [-0.1, -0.48]), ...L([0.1, 0.48], [0.5, -0.48])]; break;
    case 'profileLine': { const r = 0.48 * g; ops = [['M', cx - r, cy + 0.2 * g], ['C', cx - r, cy + 0.2 * g - r * 1.3, cx + r, cy + 0.2 * g - r * 1.3, cx + r, cy + 0.2 * g]]; break; }
    case 'profileSurface': { const r = 0.48 * g; ops = [['M', cx - r, cy + 0.2 * g], ['C', cx - r, cy + 0.2 * g - r * 1.3, cx + r, cy + 0.2 * g - r * 1.3, cx + r, cy + 0.2 * g], ['Z']]; break; }
    case 'angularity': ops = [...L([-0.5, 0.42], [0.5, 0.42]), ...L([-0.5, 0.42], [0.42, -0.42])]; break;
    case 'perpendicularity': ops = [...L([-0.5, 0.45], [0.5, 0.45]), ...L([0, 0.45], [0, -0.5])]; break;
    case 'parallelism': ops = [...L([-0.42, 0.45], [-0.02, -0.45]), ...L([0.02, 0.45], [0.42, -0.45])]; break;
    case 'position': ops = [...circle(cx, cy, 0.3 * g), ...L([-0.5, 0], [0.5, 0]), ...L([0, -0.5], [0, 0.5])]; break;
    case 'concentricity': ops = [...circle(cx, cy, 0.45 * g), ...circle(cx, cy, 0.2 * g)]; break;
    case 'symmetry': ops = [...L([-0.5, 0], [0.5, 0]), ...L([-0.3, -0.3], [0.3, -0.3]), ...L([-0.3, 0.3], [0.3, 0.3])]; break;
    case 'runout': {
      ops = L([-0.35, 0.48], [0.3, -0.35]);
      const a = Math.atan2(-0.83, 0.65); const tip = P(0.36, -0.45);
      fill.push(arrowHead(tip[0], tip[1], a, 0.36 * g, 0.14 * g));
      break;
    }
    case 'totalRunout': {
      ops = [...L([-0.5, 0.48], [0.5, 0.48]), ...L([-0.45, 0.48], [0.0, -0.3]), ...L([0.1, 0.48], [0.55, -0.3])];
      const a = Math.atan2(-0.78, 0.45);
      for (const t of [P(0.05, -0.4), P(0.6, -0.4)]) fill.push(arrowHead(t[0], t[1], a, 0.3 * g, 0.12 * g));
      break;
    }
    default: ops = [];
  }
  return { ops, fill };
}

// ---- layout of each kind: { w, h, draw(x, y) → prims, anchor: [ax, ay] (where a leader leaves, local) }
function lineWidth(el) { return el.width || Math.max(0.75, el.fontSize * 0.085); }

function layoutFcf(el) {
  const s = el.fontSize; const p = el.params || {};
  const H = s * 1.9; const pad = s * 0.45;
  const modW = s * 1.05;
  const cells = [{ kind: 'char', w: H }];
  const tolText = p.tol || '';
  const tolW = pad + (p.dia ? tw('Ø', s) + s * 0.12 : 0) + tw(tolText, s) + (p.mod ? s * 0.18 + modW : 0) + pad;
  cells.push({ kind: 'tol', w: Math.max(H, tolW) });
  for (const d of ['d1', 'd2', 'd3']) {
    if (p[d]) cells.push({ kind: 'datum', key: d, w: Math.max(H, pad + tw(p[d], s) + (p[`${d}m`] ? s * 0.15 + modW : 0) + pad) });
  }
  const w = cells.reduce((a, c) => a + c.w, 0);
  return {
    w, h: H, anchor: [0, H / 2],
    draw(x, y, stroke) {
      const prims = []; const ops = []; const fills = [];
      ops.push(...poly([[x, y], [x + w, y], [x + w, y + H], [x, y + H]]));
      let cx = x;
      for (const c of cells) {
        if (cx > x) ops.push(...seg(cx, y, cx, y + H));
        const mid = y + H / 2;
        if (c.kind === 'char') {
          const gl = gdtGlyph(p.char || 'position', cx + c.w / 2, mid, H * 0.56);
          ops.push(...gl.ops); fills.push(...gl.fill);
        } else if (c.kind === 'tol') {
          let tx = cx + pad;
          if (p.dia) { prims.push(T('Ø', tx, mid + s * 0.36, s)); tx += tw('Ø', s) + s * 0.12; }
          prims.push(T(tolText, tx, mid + s * 0.36, s)); tx += tw(tolText, s);
          if (p.mod) { tx += s * 0.18; ops.push(...circle(tx + modW / 2, mid, modW / 2)); prims.push(Tc(p.mod, tx + modW / 2, mid, s * 0.72)); }
        } else {
          const txt = p[c.key]; const m = p[`${c.key}m`];
          const inner = tw(txt, s) + (m ? s * 0.15 + modW : 0);
          let tx = cx + (c.w - inner) / 2;
          prims.push(T(txt, tx, mid + s * 0.36, s)); tx += tw(txt, s);
          if (m) { tx += s * 0.15; ops.push(...circle(tx + modW / 2, mid, modW / 2)); prims.push(Tc(m, tx + modW / 2, mid, s * 0.72)); }
        }
        cx += c.w;
      }
      return [{ k: 'path', ops, stroke, width: lineWidth(el), cap: 'round', join: 'miter' }, ...fills.map((f) => ({ k: 'path', ops: f, fill: stroke })), ...prims.map((t) => ({ ...t, color: stroke }))];
    },
  };
}

function layoutDatum(el) {
  const s = el.fontSize; const p = el.params || {};
  const B = Math.max(s * 1.8, tw(p.letter || 'A', s) + s * 0.8);
  return {
    w: B, h: B, anchor: [B / 2, B],
    draw(x, y, stroke) {
      return [
        { k: 'path', ops: poly([[x, y], [x + B, y], [x + B, y + B], [x, y + B]]), stroke, width: lineWidth(el), join: 'miter' },
        { ...Tc(p.letter || 'A', x + B / 2, y + B / 2, s), color: stroke, bold: true },
      ];
    },
  };
}

function layoutBalloon(el) {
  const s = el.fontSize; const p = el.params || {};
  const D = Math.max(s * 2, tw(p.num || '1', s) + s * 0.9);
  return {
    w: D, h: D, anchor: [D / 2, D / 2], round: D / 2,
    draw(x, y, stroke) {
      return [
        { k: 'path', ops: circle(x + D / 2, y + D / 2, D / 2), stroke, width: lineWidth(el), fill: '#ffffff' },
        { ...Tc(p.num || '1', x + D / 2, y + D / 2, s), color: stroke },
      ];
    },
  };
}

function layoutFinish(el) {
  const s = el.fontSize; const p = el.params || {};
  const a = s * 1.5; const b = s * 3;
  const c60 = 0.5; const s60 = Math.sqrt(3) / 2;
  const value = p.value || ''; const method = p.method || '';
  const ext = (value || method) ? Math.max(tw(value, s * 0.85), tw(method, s * 0.85)) + s * 0.6 : 0;
  // O (the point on the surface) sits at (a·c60, b·s60) inside the box
  const ox = a * c60; const oy = b * s60;
  const w = ox + b * c60 + ext; const h = oy + (method ? 0 : 0);
  return {
    w, h, anchor: [ox, oy],
    draw(x, y, stroke) {
      const O = [x + ox, y + oy];
      const A = [O[0] - a * c60, O[1] - a * s60];
      const Bp = [O[0] + b * c60, O[1] - b * s60];
      const ops = [...poly([A, O, Bp], false)];
      const prims = [];
      if (p.finishType === 'removal') ops.push(...seg(A[0], A[1], O[0] + a * c60, A[1]));
      if (p.finishType === 'noremoval') ops.push(...circle(O[0], O[1] - a * s60 * 0.42, a * 0.3));
      if (ext) {
        ops.push(...seg(Bp[0], Bp[1], Bp[0] + ext, Bp[1]));
        if (method) prims.push(T(method, Bp[0] + s * 0.3, Bp[1] - s * 0.25, s * 0.85));
        if (value) prims.push(T(value, Bp[0] + s * 0.3, Bp[1] + s * 0.95, s * 0.85));
      }
      return [{ k: 'path', ops, stroke, width: lineWidth(el), cap: 'round', join: 'round' }, ...prims.map((t) => ({ ...t, color: stroke }))];
    },
  };
}

function layoutWeld(el) {
  const s = el.fontSize; const p = el.params || {};
  const ref = s * 5.5; const tri = s * 1.1;
  const sizeW = p.size ? tw(p.size, s * 0.85) + s * 0.3 : 0;
  const lenW = p.length ? tw(p.length, s * 0.85) + s * 0.3 : 0;
  const tailW = p.tail ? s * 1.1 + tw(p.tail, s * 0.85) + s * 0.2 : 0;
  const lineLen = Math.max(ref, s * 1.6 + sizeW + tri + lenW + s * 1.2);
  const up = s * (p.field ? 2.4 : 1.6); const down = s * 1.6;
  const w = lineLen + tailW; const h = up + down;
  return {
    w, h, anchor: [0, up],
    draw(x, y, stroke) {
      const ry = y + up; const ops = []; const fills = []; const prims = [];
      ops.push(...seg(x, ry, x + lineLen, ry));
      const sx = x + s * 1.6 + sizeW;                     // where the weld symbol starts
      const sides = p.side === 'both' ? [1, -1] : p.side === 'other' ? [-1] : [1];   // 1 = below (arrow side)
      for (const d of sides) {
        const t = d * tri;
        switch (p.wtype) {
          case 'vgroove': ops.push(...poly([[sx, ry + t], [sx + tri / 2, ry], [sx + tri, ry + t]], false)); break;
          case 'square': ops.push(...seg(sx + tri * 0.25, ry, sx + tri * 0.25, ry + t), ...seg(sx + tri * 0.75, ry, sx + tri * 0.75, ry + t)); break;
          case 'bevel': ops.push(...seg(sx, ry, sx, ry + t), ...seg(sx, ry + t * 0.05, sx + tri, ry + t)); break;
          default: ops.push(...poly([[sx, ry], [sx, ry + t], [sx + tri, ry]])); break;   // fillet: vertical leg on the left
        }
        const ty = d > 0 ? ry + tri * 0.75 + s * 0.3 : ry - tri * 0.35;
        if (p.size) prims.push(T(p.size, sx - sizeW, ty, s * 0.85));
        if (p.length) prims.push(T(p.length, sx + tri + s * 0.3, ty, s * 0.85));
      }
      if (p.around) ops.push(...circle(x, ry, s * 0.42));
      if (p.field) {
        ops.push(...seg(x, ry, x, ry - s * 2.2));
        fills.push(poly([[x, ry - s * 2.2], [x + s * 1.0, ry - s * 1.85], [x, ry - s * 1.5]]));
      }
      if (p.tail) {
        const tx = x + lineLen;
        ops.push(...seg(tx, ry, tx + s * 0.8, ry - s * 0.8), ...seg(tx, ry, tx + s * 0.8, ry + s * 0.8));
        prims.push(T(p.tail, tx + s * 1.1, ry + s * 0.3, s * 0.85));
      }
      return [{ k: 'path', ops, stroke, width: lineWidth(el), cap: 'round', join: 'round' }, ...fills.map((f) => ({ k: 'path', ops: f, fill: stroke })), ...prims.map((t) => ({ ...t, color: stroke }))];
    },
  };
}

function layoutCenter(el) {
  const s = el.fontSize; const L = s * 3;
  return {
    w: L, h: L, anchor: null,
    draw(x, y, stroke) {
      const cx = x + L / 2; const cy = y + L / 2; const ops = [];
      const parts = [[-0.5, -0.3], [-0.12, 0.12], [0.3, 0.5]];
      for (const [a, b] of parts) { ops.push(...seg(cx + a * L, cy, cx + b * L, cy)); ops.push(...seg(cx, cy + a * L, cx, cy + b * L)); }
      return [{ k: 'path', ops, stroke, width: lineWidth(el) * 0.85, cap: 'butt' }];
    },
  };
}

// The text of a dimension: what it says, with its tolerance.
export function dimensionText(p) {
  const pre = p.prefix === 'dia' ? 'Ø' : p.prefix === 'rad' ? 'R' : p.prefix === 'sdia' ? 'SØ' : '';
  const v = `${pre}${p.value || ''}`;
  switch (p.tolType) {
    case 'sym': return { main: `${v} ±${p.plus || '0'}` };
    case 'bilat': return { main: v, upper: `+${String(p.plus || '0').replace(/^\+/, '')}`, lower: `-${String(p.minus || '0').replace(/^[-−]/, '')}` };
    case 'limit': return { upper: `${pre}${p.plus || ''}`, lower: `${pre}${p.minus || ''}` };
    case 'basic': return { main: v, boxed: true };
    default: return { main: v };
  }
}

// Dimension: P0 = (x, y), P1 = (x + w, y); the dimension line sits `offset` away.
export function dimensionPrims(el, flip) {
  const s = el.fontSize; const p = el.params || {};
  const o = Number.isFinite(p.offset) ? p.offset : -40;
  const x0 = el.x; const x1 = el.x + el.w; const y = el.y; const yl = y + o;
  const sg = o < 0 ? -1 : 1;
  const gap = Math.min(s * 0.4, Math.abs(o) * 0.3); const ext = s * 0.6;
  const stroke = el.stroke; const lw = Math.max(0.6, lineWidth(el) * 0.85);
  const ops = [...seg(x0, y + sg * gap, x0, yl + sg * ext), ...seg(x1, y + sg * gap, x1, yl + sg * ext), ...seg(x0, yl, x1, yl)];
  const aLen = Math.min(s * 0.9, el.w / 3); const aHalf = aLen * 0.3;
  const fills = el.w > 0.5 ? [arrowHead(x0, yl, Math.PI, aLen, aHalf), arrowHead(x1, yl, 0, aLen, aHalf)] : [];
  const t = dimensionText(p);
  const small = s * 0.7;
  const mainW = t.main ? tw(t.main, s) : 0;
  const stackW = (t.upper || t.lower) ? Math.max(tw(t.upper, small), tw(t.lower, small)) : 0;
  const totalW = mainW + (t.main && stackW ? s * 0.2 : 0) + stackW;
  const cx = (x0 + x1) / 2; const base = yl - s * 0.35;      // text sits above the dimension line
  const left = cx - totalW / 2;
  const texts = [];
  if (t.main) texts.push(T(t.main, left, base, s));
  if (t.upper || t.lower) {
    const sx = left + mainW + (t.main ? s * 0.2 : 0);
    if (t.main) { texts.push(T(t.upper, sx, base - s * 0.45, small)); texts.push(T(t.lower, sx, base + s * 0.25, small)); } else {
      texts.push(T(t.upper, sx, base - s * 0.75, small)); texts.push(T(t.lower, sx, base, small));
    }
  }
  if (t.boxed) ops.push(...poly([[left - s * 0.25, base - s * 1.0], [left + mainW + s * 0.25, base - s * 1.0], [left + mainW + s * 0.25, base + s * 0.3], [left - s * 0.25, base + s * 0.3]]));
  // upside down on the page? turn the words the right way up about their middle
  const textCy = base - s * 0.35;
  const rot = flip ? { rot: Math.PI, ox: cx, oy: textCy } : {};
  return [
    { k: 'path', ops, stroke, width: lw, cap: 'butt' },
    ...fills.map((f) => ({ k: 'path', ops: f, fill: stroke })),
    ...texts.map((tt) => ({ ...tt, color: stroke, ...rot })),
  ];
}
// The local box a dimension covers (for selection, hits, bounds).
export function dimensionBox(el) {
  const s = el.fontSize; const o = Number.isFinite(el.params?.offset) ? el.params.offset : -40;
  const t = dimensionText(el.params || {});
  const tw2 = Math.max(tw(t.main || '', s) + tw(t.upper || t.lower || '', s * 0.7), el.w);
  const top = Math.min(el.y, el.y + o - s * 1.6); const bottom = Math.max(el.y, el.y + o + s * 0.6);
  const cx = el.x + el.w / 2;
  const left = Math.min(el.x - s * 0.3, cx - tw2 / 2); const right = Math.max(el.x + el.w + s * 0.3, cx + tw2 / 2);
  return { x: left, y: top, w: right - left, h: bottom - top };
}

const LAYOUTS = { fcf: layoutFcf, datum: layoutDatum, balloon: layoutBalloon, finish: layoutFinish, weld: layoutWeld, center: layoutCenter };
export const layoutSymbol = (el) => (LAYOUTS[el.kind] || layoutCenter)(el);

// Where a symbol's leader leaves its box (local), nearest to the leader's end.
export function leaderStart(el, lay) {
  if (!el.leader) return null;
  const lx = el.x + el.leader[0]; const ly = el.y + el.leader[1];
  if (el.kind === 'balloon') {
    const cx = el.x + lay.w / 2; const cy = el.y + lay.h / 2; const r = lay.w / 2;
    const a = Math.atan2(ly - cy, lx - cx);
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  }
  if (el.kind === 'datum') return [el.x + lay.w / 2, ly > el.y + lay.h / 2 ? el.y + lay.h : el.y];
  if (el.kind === 'fcf' || el.kind === 'weld') {
    const leftSide = lx < el.x + lay.w / 2;
    return [leftSide ? el.x : el.x + (el.kind === 'weld' ? 0 : lay.w), el.y + (lay.anchor ? lay.anchor[1] : lay.h / 2)];
  }
  if (lay.anchor) return [el.x + lay.anchor[0], el.y + lay.anchor[1]];
  return [el.x + lay.w / 2, el.y + lay.h / 2];
}

// Every primitive of a symbol (local, unrotated): leader first, then the symbol.
export function symbolPrims(el, flip) {
  if (el.kind === 'dimension') return dimensionPrims(el, flip);
  const lay = layoutSymbol(el);
  const prims = [];
  if (el.leader) {
    const [sx, sy] = leaderStart(el, lay);
    const lx = el.x + el.leader[0]; const ly = el.y + el.leader[1];
    const s = el.fontSize; const lw = lineWidth(el);
    prims.push({ k: 'path', ops: seg(sx, sy, lx, ly), stroke: el.stroke, width: lw, cap: 'round' });
    const a = Math.atan2(ly - sy, lx - sx);
    if (el.kind === 'datum') {
      // the datum triangle: its base on the feature, its point on the leader
      const t = s * 0.9; const n = [-Math.sin(a), Math.cos(a)];
      prims.push({ k: 'path', ops: poly([[lx - Math.cos(a) * t, ly - Math.sin(a) * t], [lx + n[0] * t * 0.6, ly + n[1] * t * 0.6], [lx - n[0] * t * 0.6, ly - n[1] * t * 0.6]]), fill: el.stroke });
    } else if (el.kind === 'balloon') {
      prims.push({ k: 'path', ops: circle(lx, ly, s * 0.2), fill: el.stroke });
    } else {
      prims.push({ k: 'path', ops: arrowHead(lx, ly, a, s * 0.9, s * 0.28), fill: el.stroke });
    }
  }
  prims.push(...lay.draw(el.x, el.y, el.stroke));
  return prims;
}

// The box a symbol's own drawing covers (without its leader).
export function symbolSize(el) {
  if (el.kind === 'dimension') return null;
  const lay = layoutSymbol(el);
  return { w: lay.w, h: lay.h };
}
